<template>
  <div>
    <h2 class="only-print">{{ calendarName }}</h2>
    <select class="no-desktop no-print" v-model="selectedCalendarId" @change="nativeSelect">
      <option v-for="calendar in sortedCalendars" :value="calendar.id">{{ calendar.name }}</option>
    </select>
    <div class="d-container no-mobile no-print" :class="{'d-container--active': isDropDown}">
      <div class="d-select__selected" @click="setDropdown" v-on-clickaway="setDropdownFromClick" ref="d_parent">
        <h1>{{ calendarName }}</h1>
        <div class="d-spinner" v-if="isSelectLoading"></div>
        <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="tw-icon tw-icon-cheveron-selection">
          <path fill-rule="evenodd"
                d="M8.7 9.7a1 1 0 1 1-1.4-1.4l4-4a1 1 0 0 1 1.4 0l4 4a1 1 0 1 1-1.4 1.4L12 6.42l-3.3 3.3zm6.6 4.6a1 1 0 0 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 1.4-1.4l3.3 3.29 3.3-3.3z"
                class="secondary"></path>
        </svg>
      </div>
      <div class="d-select__dropdown" v-if="isDropDown" ref="d_child"
           :class="{'d-dropdown--animate': isPopperReady, 'd-dropdown--show': isPopperDone}">
        <div class="d-select-input" @click.stop ref="ref_d_select_input_container">
          <span class="icon-search5"></span>
          <input type="search" v-model="search" ref="inputsearch">
        </div>
        <ul class="d-select__dropdown__container" ref="ref_d_select_calendar_dropdown">
          <li class="d-list-item"
              v-for="calendar in calendarQuery"
              @click="selectCalendar(calendar.id)"
              @mouseenter="setTheKeyboardSelected(calendar)"
              @mousemove="onMouseMove"
              :class="[
                {'d-list-item--highlighted': getIsTheKeyboardSelected(calendar)},
                {'d-list-item--selected': calendar.v_selected}
                ]"
              :key="calendar.id">
            {{ calendar.name }} <span class="icon-checkmark-circle"></span>
          </li>
          <li class="d-select__dropdown__spinner-container" v-if="isSchoolCalendarsLoading">
            <div class="d-spinner"></div>
          </li>
          <li class="d-list-item text-explainer" v-if="calendarQuery.length === 0 && !isSchoolCalendarsLoading">{{
              $t('vue2.common.no_calendars_found')
            }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import {mixin as clickaway} from 'vue-clickaway'
import {createPopper} from '@popperjs/core'
import Loader from '../common/Loader'

export default {
  components: {Loader},
  mixins: [clickaway],
  props: {
    calendar: {
      required: true,
      default: () => null
    }
  },
  data () {
    return {
      selectedCalendarId: null,
      isDropDown: false,
      isPopperDone: false,
      isPopperReady: false,
      search: '',
      isKeyboardShortcutsActivated: false,
      keyboardSelected: null,
      isMouseMoved: false,
      isSelectLoading: false
    }
  },
  computed: {
    isSchoolCalendarsLoading () {
      return this.$store.state.isLoadingSchoolCalenders
    },
    calendarName () {
      return this.calendar ? this.calendar.name : ''
    },
    klassCalendars () {
      return this.$store.getters.klassCalendars
    },
    userCalendars () {
      return this.$store.getters.userCalendars
    },
    roomCalendars () {
      return this.$store.getters.roomCalendars
    },
    mergeCalendars () {
      return this.$store.getters.mergeCalendars
    },
    otherCalendars () {
      return this.$store.getters.otherCalendars
    },
    clubCalendars () {
      return this.$store.getters.clubCalendars
    },
    studentCalendars () {
      return this.$store.getters.studentCalendars
    },
    kindergartenCalendars () {
      return this.$store.getters.kindergartenCalendars
    },
    sortedCalendars () {
      if (this.calendar) {
        switch (this.calendar.type) {
          case 'klass':
            return this.klassCalendars
          case 'user':
            return this.userCalendars
          case 'room':
            return this.roomCalendars
          case 'merge':
            return this.mergeCalendars
          case 'other':
            return this.otherCalendars
          case 'club':
            return this.clubCalendars
          case 'student':
            return this.studentCalendars
          case 'kindergarten':
            return this.kindergartenCalendars
          default:
            return []
        }
      } else {
        return []
      }
    },
    sortedCalendarsWithVProps () {
      return this.sortedCalendars.map(calendar => {
        return {
          ...calendar,
          v_selected: this.selectedCalendarId === calendar.id
        }
      })
    },
    calendarQuery () {
      return this.sortedCalendarsWithVProps.filter(item => {
        return item.name !== null ? item.name.match(new RegExp(this.search, 'i')) : ''
      })
    }
  },
  watch: {
    calendarQuery () {
      this.keyboardSelected = this.calendarQuery[0] ? this.calendarQuery[0] : null
    }
  },
  methods: {
    onMouseMove () {
      this.isMouseMoved = true
    },
    setTheKeyboardSelected (calendar) {
      if (!this.isMouseMoved) {
        return
      }
      this.keyboardSelected = calendar
    },
    getIsTheKeyboardSelected (calendar) {
      if (!this.keyboardSelected) {
        return false
      } else {
        return this.keyboardSelected.id === calendar.id
      }
    },
    nativeSelect () {
      window.location = '/calendars/' + this.selectedCalendarId
    },
    selectCalendar (calendarId) {
      this.isSelectLoading = true
      this.selectedCalendarId = calendarId
      window.location = '/calendars/' + calendarId
      this.setDropdown()
    },
    createPopper () {
      this.isPopperReady = false
      this.isPopperDone = false
      let dChild = this.$refs.d_child
      let dParent = this.$refs.d_parent
      if (this.appendToBody) {
        document.getElementById('djs-body-click').appendChild(dChild)
      }
      createPopper(dParent, dChild, {
        placement: 'bottom-start',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 0]
            }
          }
        ]
      })
      setTimeout(_ => {
        this.isPopperReady = true
        this.isPopperDone = true
      }, 0)
      setTimeout(_ => {
        this.$refs.inputsearch.focus()
      }, 200)
    },
    setDropdown () {
      this.isDropDown = !this.isDropDown
      if (this.isDropDown) {
        setTimeout(_ => {
          this.createPopper()
          this.addKeyboardEvents()
          this.keyboardSelected = this.calendarQuery.length > 0 ? this.calendarQuery[0] : null
        }, 0)
      } else {
        this.isPopperReady = false
        this.isPopperDone = false
        this.search = ''
        this.destroyKeyboardEvents()
      }
    },
    setDropdownFromClick () {
      if (!this.isDropDown) {
        return
      }
      this.setDropdown()
    },
    handleKeyboardNavigation (event) {
      const key = event.key

      // Handle keyboard navigation only when the dropdown is active
      if (this.isDropDown) {
        if (key === 'Escape') {
          this.setDropdownFromClick()
          return
        }
        if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter') {
          if (this.calendarQuery.length === 0) {
            return
          }
          const idx = this.keyboardSelected ? this.calendarQuery.findIndex(cal => cal.id === this.keyboardSelected.id) : 0
          if (key === 'Enter') {
            if (!this.keyboardSelected) {
              return
            }
            this.selectCalendar(this.keyboardSelected.id)
          }
          if (key === 'ArrowDown') {
            if (this.calendarQuery.length === (idx + 1)) {
              return
            }
            this.keyboardSelected = this.calendarQuery[idx + 1]
            this.scrollToKeyboardSelected()
          }
          if (key === 'ArrowUp') {
            if (idx === 0) {
              return
            }
            this.keyboardSelected = this.calendarQuery[idx - 1]
            this.scrollToKeyboardSelected()
          }
        }
      }
    },
    scrollToKeyboardSelected () {
      setTimeout(() => {
        const searchFieldHeight = this.$refs.ref_d_select_input_container.getBoundingClientRect().height

        const highlightedCalendar = document.querySelector('.d-list-item.d-list-item--highlighted')
        const highlightedOffsetTop = highlightedCalendar.offsetTop
        const highlightedHeight = highlightedCalendar.getBoundingClientRect().height
        const totalHeight = (highlightedOffsetTop + highlightedHeight) - searchFieldHeight

        const dropdownScrollTop = this.$refs.ref_d_select_calendar_dropdown.scrollTop
        const dropdownHeight = this.$refs.ref_d_select_calendar_dropdown.getBoundingClientRect().height
        const dropdownBottom = dropdownScrollTop + dropdownHeight
        if (totalHeight > dropdownBottom) {
          this.isMouseMoved = false
          this.$refs.ref_d_select_calendar_dropdown.scrollTop = this.$refs.ref_d_select_calendar_dropdown.scrollTop + highlightedHeight
        }

        if ((highlightedOffsetTop - searchFieldHeight) < dropdownScrollTop) {
          this.isMouseMoved = false
          this.$refs.ref_d_select_calendar_dropdown.scrollTop = this.$refs.ref_d_select_calendar_dropdown.scrollTop - highlightedHeight
        }
      }, 0)
    },
    addKeyboardEvents () {
      if (!this.isKeyboardShortcutsActivated) {
        window.addEventListener('keydown', this.handleKeyboardNavigation)
        this.isKeyboardShortcutsActivated = true
      }
    },
    destroyKeyboardEvents () {
      window.addEventListener('keydown', this.handleKeyboardNavigation)
      this.isKeyboardShortcutsActivated = false
    }
  },
  mounted () {
    if (this.calendar) {
      this.selectedCalendarId = this.calendar.id
    }
  },
  beforeDestroy () {
    // remove the dropdown from the DOM
    let dChild = this.$refs.d_child
    if (dChild) {
      dChild.remove()
    }
    this.destroyKeyboardEvents()
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.d-container {
  position: relative;
}

.d-select__selected {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  padding: 0.5rem 0.5rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  cursor: pointer;
  min-width: 220px;
}

.d-container--active .d-select__selected {
  min-width: 250px;
}

.d-select__selected h1 {
  padding-right: 0.5rem;
  margin: 0;
  font-size: 1.2rem;
}

.d-container.d-container--active .d-select__selected {
  background: #ffffff;
}

.tw-icon {
  width: 1.3rem;
}

.d-container--active .d-select__selected {
  border-color: var(--blue);
  border-radius: 4px 4px 0 0;
}

.d-select__dropdown {
  position: absolute;
  z-index: 19990;
  top: calc(100% - 3px);
  left: 0;
  background-color: #fff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, .24);
  height: auto;
  outline: none;
  text-align: left;
  padding: 0;
  min-width: 100px;
  width: 250px;
  user-select: none;
  opacity: 0;
  margin-top: -10px !important;
  border: 1px solid var(--purple);
  border-width: 0 1px 1px 1px;
  border-radius: 0 0 4px 4px;
}

.d-select__dropdown.d-dropdown--animate {
  transition: opacity 0.15s, margin-top 0.15s;
}

.d-select__dropdown.d-dropdown--show {
  opacity: 1;
  margin-top: 0px !important;
}

.d-select__dropdown .d-select-input {
  position: relative;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
}

.d-select-input > span {
  position: absolute;
  top: calc(0.5rem + 0.65rem);
  left: calc(0.5rem + 1rem);
  color: var(--grey);
  font-size: 1.2rem;
}

.d-select__dropdown input {
  padding: 0.5rem 0.5rem 0.5rem 2.5rem;
  font-size: 13px;
  font-weight: normal;
  border-color: var(--border-color);
  box-sizing: border-box;
}

.d-select__dropdown input:focus {
  border-color: var(--purple);
}

.d-select__dropdown > ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.d-select__dropdown > ul > li {
  cursor: pointer;
  font-size: 13px;
  font-weight: normal;
  padding: 0.5rem 1rem;
  color: var(--dark-blue);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.d-select__dropdown > ul > li:last-child {
  border-radius: 0 0 4px 4px;
}

.d-select__dropdown > ul > li.text-explainer {
  margin-bottom: 0;
  background-color: initial !important;
  cursor: default;
}

.d-list-item span {
  opacity: 0;
}

.d-list-item.d-list-item--selected span {
  opacity: 1;
}

.d-list-item.d-list-item--selected:not(.d-list-item--highlighted) {
  color: var(--primary);
}

.d-list-item.d-list-item--highlighted {
  color: #ffffff;
  background-color: var(--purple) !important;
}

.d-select__dropdown__container {
  max-height: 50vh;
  overflow-y: auto;
}

.d-spinner {
  position: relative;
  background: rgba(255, 255, 255, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  height: 20px;
  width: 20px;
}

.d-spinner {
  border: 2px solid #d0d0d0; /* Light grey */
  border-top: 2px solid var(--purple); /* Blue */
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 0.7s linear infinite;
}

.d-select__dropdown__spinner-container {
  display: flex;
  justify-content: center;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>



// WEBPACK FOOTER //
// DCalendarSelect.vue?2a63e338